import { RequestHandler, rest } from 'msw';
import { meliAuthLink } from './fixtures/auth-links';
import { criteria, editedCriteria, newCriteria } from './fixtures/criterias';
import { carriersMeTiendaNubeMe, deliveriesTiendaNubeMe, pickupMeliMe, pickupShopifyMe } from './fixtures/pickup';
import { authorizedProviders } from './fixtures/providers';
import { shopifyAuth, shopifyAuthLink, tiendNubeAuthLink } from './fixtures/shared';
import { delayedResponse } from './responses/delayedResponse';
import { API_PATHS } from '../api-integrations-paths';
import {
    EditCriteriaResponse,
    FetchUserCriteriaResponse,
    FetchUserIntegrationUrlResponse,
    PostCriteriaResponse,
    FetchPickupMeliResponse,
    FetchPickupShopifyResponse,
    FetchAuthorizedProviders,
    FetchShopifyAuthResponse,
    FetchShopifyIntegrationUrlResponse,
    FetchDeliveriesTiendaNubeResponse,
    FetchCarriersMeTiendaNubeResponse,
} from '../responses';

const RUNTIME_URL = process.env['NX_INTEGRATIONS_API_URL'] || 'http://localhost';

const PATHS = {
    fetchAuthorizedProviders: new RegExp(`${RUNTIME_URL}/${API_PATHS.PROVIDERS.ME}`),
    fetchUserCriteria: new RegExp(`${RUNTIME_URL}/meli/criteria`),
    postUserCriteria: new RegExp(`${RUNTIME_URL}/meli/criteria/zone$`),
    editUserCriteria: new RegExp(`${RUNTIME_URL}/meli/criteria/zone/[0-9a-zA-Z]+$`),
    fetchUserIntegrationLink: new RegExp(`${RUNTIME_URL}/meli/auth-link$`),
    fetchPickupMeli: new RegExp(`${RUNTIME_URL}/${API_PATHS.MELI.PICKUP.ME}`),
    fetchPickupShopify: new RegExp(`${RUNTIME_URL}/${API_PATHS.SHOPIFY.PICKUP.ME}`),
    fetchShopifyAuth: new RegExp(`${RUNTIME_URL}/${API_PATHS.SHOPIFY.AUTH}`),
    fetchMeliAuth: new RegExp(`${RUNTIME_URL}/${API_PATHS.MELI.AUTH}`),
    fetchTiendaNubeAuth: new RegExp(`${RUNTIME_URL}/${API_PATHS.TIENDA_NUBE.AUTH}`),
    fetchShopifyIntegrationUrl: new RegExp(`${RUNTIME_URL}/${API_PATHS.SHOPIFY.AUTH_LINK}`),
    fetchTiendaNubeIntegrationUrl: new RegExp(`${RUNTIME_URL}/${API_PATHS.TIENDA_NUBE.AUTH_LINK}`),
    fetchMeliIntegrationUrl: new RegExp(`${RUNTIME_URL}/${API_PATHS.MELI.AUTH_LINK}`),
    fetchDeliveryTiendaNube: new RegExp(`${RUNTIME_URL}/${API_PATHS.TIENDA_NUBE.DELIVERY.ME}`),
    fetchCarriersMeTiendaNube: new RegExp(`${RUNTIME_URL}/${API_PATHS.TIENDA_NUBE.CARRIERS.ME}`),
};

export const fetchAuthorizedProviders = rest.get(PATHS.fetchAuthorizedProviders, (_, res, ctx) => {
    return delayedResponse(ctx.json<FetchAuthorizedProviders>(authorizedProviders));
});

export const fetchUserCriteria = rest.get(PATHS.fetchUserCriteria, (_, res, ctx) => {
    return delayedResponse(ctx.json<FetchUserCriteriaResponse>(criteria));
});

export const postUserCriteria = rest.post(PATHS.postUserCriteria, (_, _res, ctx) => {
    return delayedResponse(ctx.json<PostCriteriaResponse>(newCriteria));
});

export const editUserCriteria = rest.patch(PATHS.editUserCriteria, (_, _res, ctx) => {
    return delayedResponse(ctx.json<EditCriteriaResponse>(editedCriteria));
});

export const fetchUserIntegrationLink = rest.get(PATHS.fetchUserIntegrationLink, (_, _res, ctx) => {
    return delayedResponse(ctx.json<FetchUserIntegrationUrlResponse>(meliAuthLink));
});

export const fetchPickupMeli = rest.get(PATHS.fetchPickupMeli, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchPickupMeliResponse>(pickupMeliMe));
});

export const fetchPickupShopify = rest.get(PATHS.fetchPickupShopify, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchPickupShopifyResponse>(pickupShopifyMe));
});

export const fetchMeliAuth = rest.post(PATHS.fetchMeliAuth, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyAuthResponse>(shopifyAuth));
});

export const fetchTiendaNubeAuth = rest.post(PATHS.fetchTiendaNubeAuth, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyAuthResponse>(shopifyAuth));
});

export const fetchShopifyAuth = rest.post(PATHS.fetchShopifyAuth, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyAuthResponse>(shopifyAuth));
});

export const fetchShopifyIntegrationUrl = rest.get(PATHS.fetchShopifyIntegrationUrl, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyIntegrationUrlResponse>(shopifyAuthLink));
});

export const fetchTiendaNubeIntegrationUrl = rest.get(PATHS.fetchTiendaNubeIntegrationUrl, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyIntegrationUrlResponse>(tiendNubeAuthLink));
});

export const fetchMeliIntegrationUrl = rest.get(PATHS.fetchMeliIntegrationUrl, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchShopifyIntegrationUrlResponse>(tiendNubeAuthLink));
});

export const fetchDeliveryTiendaNube = rest.get(PATHS.fetchDeliveryTiendaNube, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchDeliveriesTiendaNubeResponse>(deliveriesTiendaNubeMe));
});

export const fetchCarriersMeTiendaNube = rest.get(PATHS.fetchCarriersMeTiendaNube, (req, _res, ctx) => {
    return delayedResponse(ctx.json<FetchCarriersMeTiendaNubeResponse>(carriersMeTiendaNubeMe));
});

export const handlers: RequestHandler[] = [
    fetchAuthorizedProviders,
    fetchUserCriteria,
    postUserCriteria,
    editUserCriteria,
    fetchUserIntegrationLink,
    fetchPickupMeli,
    fetchPickupShopify,
    fetchShopifyAuth,
    fetchShopifyIntegrationUrl,
    fetchTiendaNubeIntegrationUrl,
    fetchMeliIntegrationUrl,
    fetchDeliveryTiendaNube,
    fetchCarriersMeTiendaNube,
    fetchTiendaNubeAuth,
    fetchMeliAuth,
];

export const handlersWithErrors = {
    fetchAuthorizedProviders: rest.get(PATHS.fetchAuthorizedProviders, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
    fetchUserCriteria: rest.get(PATHS.fetchUserCriteria, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
    postUserCriteria: rest.post(PATHS.postUserCriteria, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
    editUserCriteria: rest.patch(PATHS.editUserCriteria, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
    fetchUserIntegrationLink: rest.get(PATHS.fetchUserIntegrationLink, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
    fetchShopifyIntegrationUrl: rest.get(PATHS.fetchShopifyIntegrationUrl, (_, _res, ctx) => {
        return delayedResponse(ctx.status(403), ctx.json({}));
    }),
};
